import React from 'react'
import { Star, Download, ExternalLink } from 'lucide-react'
import {
  ActionButtons,
  JobActionButtons,
  EditIcon,
  DeleteIcon,
  SeeIcon,
} from 'components/Buttons/ActionButtons'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
  createCopyAction,
  createToggleActiveAction,
  createExternalAction,
  createDownloadAction,
} from 'components/Buttons/ActionsDropdown'
import {
  Preview,
  Section,
  Row,
  Col,
} from 'component-catalog/previews/PreviewKit'

// ---------------------------------------------------------------------------
// ActionButtons — inline icon button set
// ---------------------------------------------------------------------------
function ActionButtonsPreview(): React.ReactElement {
  return (
    <>
      <Section label="Full set">
        <Col label="Edit + Delete + See (all three)">
          <ActionButtons
            onEdit={() => {}}
            onDelete={() => {}}
            onSee={() => {}}
          />
        </Col>
        <Col label="Edit + Delete only">
          <ActionButtons onEdit={() => {}} onDelete={() => {}} />
        </Col>
        <Col label="Edit only">
          <ActionButtons onEdit={() => {}} />
        </Col>
        <Col label="See only">
          <ActionButtons onSee={() => {}} />
        </Col>
      </Section>
      <Section label="Disabled states">
        <Col label="Edit disabled">
          <ActionButtons onEdit={() => {}} onDelete={() => {}} editDisabled />
        </Col>
        <Col label="Delete disabled">
          <ActionButtons onEdit={() => {}} onDelete={() => {}} deleteDisabled />
        </Col>
        <Col label="Both disabled">
          <ActionButtons
            onEdit={() => {}}
            onDelete={() => {}}
            editDisabled
            deleteDisabled
          />
        </Col>
      </Section>
      <Section label="prepend / append slots">
        <Col label="prepend — extra content before icons">
          <ActionButtons
            onEdit={() => {}}
            onDelete={() => {}}
            prepend={
              <span className="text-xs text-fg-secondary bg-surface-alt border border-line px-2 py-0.5 rounded">
                Row 1
              </span>
            }
          />
        </Col>
        <Col label="append — extra content after icons">
          <ActionButtons
            onEdit={() => {}}
            onDelete={() => {}}
            append={
              <button className="text-xs text-brand hover:underline">
                View details
              </button>
            }
          />
        </Col>
      </Section>
      <Section label="Custom icons (icon prop per slot)">
        <Col label="Custom seeIcon (Star), editIcon (Download)">
          <ActionButtons
            onEdit={() => {}}
            onSee={() => {}}
            seeIcon={Star as any}
            editIcon={Download as any}
          />
        </Col>
      </Section>
      <Section label="Individual icon exports">
        <Row label="EditIcon · DeleteIcon · SeeIcon used standalone">
          <EditIcon onClick={() => {}} />
          <DeleteIcon onClick={() => {}} />
          <SeeIcon onClick={() => {}} />
        </Row>
        <Row label="Disabled variants">
          <EditIcon onClick={() => {}} disabled />
          <DeleteIcon onClick={() => {}} disabled />
        </Row>
      </Section>
      <Section label="JobActionButtons — view-only variant">
        <Row>
          <JobActionButtons onView={() => {}} />
        </Row>
      </Section>
    </>
  )
}

// ---------------------------------------------------------------------------
// ActionsDropdown — kebab menu with rich action items
// ---------------------------------------------------------------------------
function ActionsDropdownPreview(): React.ReactElement {
  return (
    <>
      <Section label="Full menu — click ⋮ to open">
        <Row label="Active record (enable/disable + actions)">
          <ActionsDropdown
            actions={[
              createToggleActiveAction(() => {}, true),
              createEditAction(() => {}),
              createCopyAction(() => {}),
              createExternalAction('Open in CRM', () => {}),
              createDownloadAction('Export CSV', () => {}),
              createDeleteAction(() => {}),
            ]}
          />
        </Row>
        <Row label="Inactive record">
          <ActionsDropdown
            actions={[
              createToggleActiveAction(() => {}, false),
              createEditAction(() => {}),
              createDeleteAction(() => {}),
            ]}
          />
        </Row>
      </Section>
      <Section label="With disabled action (disabledReason shown on hover)">
        <Row>
          <ActionsDropdown
            actions={[
              createEditAction(() => {}),
              {
                label: 'Delete',
                icon: ExternalLink,
                onClick: () => {},
                disabled: true,
                disabledReason:
                  'Cannot delete — record has active dependencies',
              },
            ]}
          />
        </Row>
      </Section>
      <Section label="Disabled trigger (entire dropdown locked)">
        <Row>
          <ActionsDropdown actions={[createEditAction(() => {})]} disabled />
        </Row>
      </Section>
    </>
  )
}

export function ButtonsMock(): React.ReactElement {
  return (
    <Preview>
      <ActionButtonsPreview />
      <ActionsDropdownPreview />
    </Preview>
  )
}
